/** An address must be confirmed within this foreground conversation window. */
export const VOICE_NAVIGATION_TIMEOUT_MS = 120_000;
export const VOICE_NAVIGATION_MAX_QUERY_LENGTH = 160;

export type VoiceNavigationState =
  | { phase: "idle" }
  | { phase: "address"; updatedAt: number }
  | { phase: "ready"; query: string; updatedAt: number };

export type VoiceNavigationAction =
  | { kind: "passthrough" }
  | { kind: "prompt"; message: string }
  | { kind: "search"; query: string; confirm: boolean }
  | { kind: "navigate"; query: string }
  | { kind: "select"; query: string; delta: -1 | 1 }
  | { kind: "cancel" };

export interface VoiceNavigationTransition {
  state: VoiceNavigationState;
  action: VoiceNavigationAction;
}

export const initialVoiceNavigation = (): VoiceNavigationState => ({
  phase: "idle",
});

const askAddress = "לאיזו כתובת לנווט? אמרו כתובת, ולאחר בחירת היעד אמרו אנטר.";
const expired = "החיפוש הקולי הסתיים. אמרו ניווט כדי להתחיל חיפוש חדש.";
const emptyAddress = "אמרו קודם כתובת. לאחר הצגת היעד, אמרו אנטר כדי לנווט.";
const confirmation = /^(?:אנטר|enter|אשר ניווט|אישור ניווט|צא לדרך)$/i;
const cancel = /^(?:ביטול|בטל|בטלי)(?: (?:ניווט|חיפוש|חיפוש קולי))?$/;
const next = /^(?:הבא|הבאה|התוצאה הבאה|היעד הבא)$/;
const previous = /^(?:הקודם|הקודמת|התוצאה הקודמת|היעד הקודם)$/;
const navigationStart =
  /^(?:ניווט|נווט(?:י)?(?: אותי)?|קח(?:י)? אותי)(?:\s+(.+))?$/;
const inlineConfirmation = /\s+(?:אנטר|enter)$/i;

/** Keep address punctuation (e.g. ז'בוטינסקי and 12/4), normalize STT punctuation. */
function normalize(input: string): string {
  return input
    .normalize("NFKC")
    .replace(/[\u0591-\u05bd\u05bf\u05c1-\u05c2\u05c4-\u05c5\u05c7]/g, "")
    .replace(/[.,!?؟:;…״“”()\[\]{}]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function prompt(
  state: VoiceNavigationState,
  message: string,
): VoiceNavigationTransition {
  return { state, action: { kind: "prompt", message } };
}

function captureAddress(
  address: string,
  now: number,
): VoiceNavigationTransition {
  const confirm = inlineConfirmation.test(address);
  const query = address.replace(inlineConfirmation, "").trim();
  if (
    query.length < 2 ||
    query.length > VOICE_NAVIGATION_MAX_QUERY_LENGTH ||
    !/\p{L}/u.test(query) ||
    confirmation.test(query)
  ) {
    return prompt({ phase: "address", updatedAt: now }, emptyAddress);
  }
  return {
    state: { phase: "ready", query, updatedAt: now },
    action: { kind: "search", query, confirm },
  };
}

/**
 * Consume the navigation dialogue BEFORE attempting a road-report command.
 * Only `passthrough` may be sent to parseVoiceCommand: while capturing an
 * address, even "משטרה" or "מצלמה" is address text, never a report.
 *
 * `ready` means an address was captured, not that a search succeeded. The host
 * owns asynchronous search/candidate selection and MUST match a navigate/select
 * action's query to its current successful search before using the candidate.
 * `search.confirm` records an explicit trailing "אנטר"; an ambiguous search
 * should still announce the selected result and require another confirmation.
 * Search responses must be discarded after cancellation, expiry, or replacement.
 * Reset this state when listening stops, the app hides, or navigation starts.
 */
export function advanceVoiceNavigation(
  state: VoiceNavigationState,
  input: string,
  now = Date.now(),
): VoiceNavigationTransition {
  if (!Number.isFinite(now)) return prompt(initialVoiceNavigation(), expired);

  // Bound work before Unicode normalization as well as after address extraction.
  if (input.length > 500) {
    return state.phase === "idle"
      ? { state, action: { kind: "passthrough" } }
      : prompt({ phase: "address", updatedAt: now }, emptyAddress);
  }
  const text = normalize(input);
  const start = navigationStart.exec(text);
  if (start && !/[?؟]/.test(input)) {
    let address = (start[1] || "").trim();
    // Only separate prepositions are unambiguous: לוד and לכיש are place names.
    address = address.replace(/^(?:אל|ל)\s+/, "").trim();
    return address
      ? captureAddress(address, now)
      : prompt({ phase: "address", updatedAt: now }, askAddress);
  }

  if (cancel.test(text) && state.phase !== "idle") {
    return { state: initialVoiceNavigation(), action: { kind: "cancel" } };
  }

  if (
    state.phase !== "idle" &&
    (!Number.isFinite(state.updatedAt) ||
      now < state.updatedAt ||
      now - state.updatedAt >= VOICE_NAVIGATION_TIMEOUT_MS)
  ) {
    // Do not reinterpret a late address utterance as a community report.
    return prompt(initialVoiceNavigation(), expired);
  }

  if (confirmation.test(text)) {
    if (state.phase !== "ready" || /[?؟]/.test(input)) {
      return prompt(
        state.phase === "idle" ? state : { ...state, updatedAt: now },
        state.phase === "ready"
          ? "אמרו אנטר כדי לאשר את היעד, או ביטול כדי לבטל."
          : emptyAddress,
      );
    }
    return {
      state: initialVoiceNavigation(),
      action: { kind: "navigate", query: state.query },
    };
  }

  if (state.phase === "idle") return { state, action: { kind: "passthrough" } };

  if (next.test(text) || previous.test(text)) {
    return state.phase === "ready"
      ? {
          state: { ...state, updatedAt: now },
          action: {
            kind: "select",
            query: state.query,
            delta: next.test(text) ? 1 : -1,
          },
        }
      : prompt(state, emptyAddress);
  }

  if (!text || /[?؟]/.test(input)) {
    return prompt(
      state,
      state.phase === "ready"
        ? "אמרו אנטר לאישור, הבא לבחירה אחרת, או כתובת חדשה."
        : emptyAddress,
    );
  }
  return captureAddress(text, now);
}
